// Tela padrão da Visão Geral (#6493): para quem não tem tela própria (ex.: Giovanna,
// Ludimilla, conta Monitoramento) e base de "Minhas tarefas" para as outras telas.
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, CheckSquare, ClipboardList, LifeBuoy, Plus } from 'lucide-react';
import TaskModal from '../../Tarefas/TaskModal';
import { abrirSolicitacao } from '../../../src/lib/abrirSolicitacao';
import { filaDoDia, type MinhasTarefas, type TarefaAberta } from '../../../src/lib/visaoGeral/minhasTarefas';
import { isoLocal } from '../../../src/lib/visaoGeral/periodo';
import type { TarefaCategoria } from '../../../types';
import { useMinhasTarefas } from '../useMinhasTarefas';
import { BotaoAcao, CabecalhoVisaoGeral, CartaoHoje, ListaAtalhos, PainelDetalhe, SecaoVisaoGeral, plural } from '../componentes';
import type { PropsTelaVisaoGeral } from '../tipos';
import { BlocosDoDia } from '../BlocosDoDia';

// Mesmos papéis que o RoleGuard de /tarefas e /tarefas/solicitacoes bloqueia (App.tsx).
const SEM_ACESSO_TAREFAS = new Set(['vendedor', 'parceiro', 'vendedor_parceiro', 'empresa_parceira_admin', 'empresa_parceira_operador']);

function rotuloVencimento(venc: string | null, hoje: string): { texto: string; atrasada: boolean } {
  if (!venc) return { texto: 'sem data', atrasada: false };
  const dia = venc.slice(0, 10);
  if (dia === hoje) return { texto: 'hoje', atrasada: false };
  const [a, m, d] = dia.split('-').map(Number);
  const [ha, hm, hd] = hoje.split('-').map(Number);
  const dias = Math.round((new Date(a, m - 1, d).getTime() - new Date(ha, hm - 1, hd).getTime()) / 86400000);
  if (dias < 0) return { texto: `atrasada há ${plural(-dias, 'dia', 'dias')}`, atrasada: true };
  if (dias === 1) return { texto: 'amanhã', atrasada: false };
  return { texto: `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}`, atrasada: false };
}

/** Fila do dia: atrasadas, hoje e próximos 7 dias. Reaproveitada por outras telas (dados de useMinhasTarefas). */
export const MinhaFila: React.FC<{ dados: MinhasTarefas | null; titulo?: string; aoAbrir?: (t: TarefaAberta) => void }> = ({ dados, titulo = 'Minhas tarefas', aoAbrir }) => {
  const navigate = useNavigate();
  const hoje = isoLocal(new Date());
  const abrir = aoAbrir ?? ((t: TarefaAberta) => (t.categoria === 'solicitacao' ? abrirSolicitacao(String(t.id)) : navigate(`/tarefas?id=${t.id}`)));
  const fila = dados ? filaDoDia(dados) : null;
  return (
    <PainelDetalhe titulo={titulo}>
      {fila === null ? (
        <div className="space-y-2">{[0, 1, 2].map(i => <div key={i} className="h-10 rounded-lg bg-gray-100 dark:bg-gray-700 animate-pulse" />)}</div>
      ) : fila.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Nada vencendo nos próximos 7 dias.</p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
          {fila.map(t => {
            const v = rotuloVencimento(t.vencimento, hoje);
            return (
              <li key={t.id}>
                <button type="button" onClick={() => abrir(t)} className="w-full flex items-center gap-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg px-2 -mx-2">
                  {t.categoria === 'solicitacao'
                    ? <LifeBuoy size={16} className="shrink-0 text-violet-500" aria-label="Solicitação" />
                    : <CheckSquare size={16} className="shrink-0 text-primary" aria-label="Atividade" />}
                  <span className="flex-1 min-w-0 text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{t.titulo}</span>
                  <span className={`text-xs whitespace-nowrap ${v.atrasada ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-gray-500 dark:text-gray-400'}`}>{v.texto}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {dados && (dados.semData.length > 0 || dados.depois.length > 0) && (
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-3">
          {[dados.semData.length > 0 && `${plural(dados.semData.length, 'tarefa', 'tarefas')} sem data`, dados.depois.length > 0 && `${dados.depois.length} para depois de 7 dias`].filter(Boolean).join(' · ')}
        </p>
      )}
    </PainelDetalhe>
  );
};

const TelaPadrao: React.FC<PropsTelaVisaoGeral> = ({ pessoa, souEu, definicao, periodo }) => {
  const { dados, recarregar } = useMinhasTarefas(pessoa.id);
  const [novaCategoria, setNovaCategoria] = useState<TarefaCategoria | null>(null);
  const acessaTarefas = !SEM_ACESSO_TAREFAS.has(String(pessoa.role));

  const acoes = souEu && acessaTarefas ? (
    <>
      <BotaoAcao rotulo="Nova tarefa" icone={Plus} onClick={() => setNovaCategoria('tarefa')} />
      <BotaoAcao rotulo="Abrir solicitação" icone={LifeBuoy} onClick={() => setNovaCategoria('solicitacao')} />
    </>
  ) : null;

  return (
    <div className="space-y-8">
      <CabecalhoVisaoGeral nome={pessoa.full_name} pergunta={definicao.pergunta} periodo={periodo} acoes={acoes} />

      <SecaoVisaoGeral titulo="Hoje" subtitulo="o que precisa de você agora">
        <CartaoHoje valor={dados ? dados.atrasadas.length : null} rotulo="atrasadas" tom="critico" para={acessaTarefas ? '/tarefas' : undefined} />
        <CartaoHoje valor={dados ? dados.hoje.length : null} rotulo="vencem hoje" tom="atencao" para={acessaTarefas ? '/tarefas' : undefined} />
        <CartaoHoje valor={dados ? dados.proximas.length : null} rotulo="nos próximos 7 dias" para={acessaTarefas ? '/tarefas?view=calendar' : undefined} />
        <CartaoHoje valor={dados ? dados.solicitacoes : null} rotulo="solicitações abertas comigo" para={acessaTarefas ? '/tarefas/solicitacoes' : undefined} />
      </SecaoVisaoGeral>

      <BlocosDoDia pessoa={pessoa} perfil={definicao.id} />

      <MinhaFila dados={dados} />

      {acessaTarefas && (
        <ListaAtalhos itens={[
          { rotulo: 'Atividades e Visitas', para: '/tarefas', icone: ClipboardList },
          { rotulo: 'Central de Solicitações', para: '/tarefas/solicitacoes', icone: LifeBuoy },
          { rotulo: 'Agenda', para: '/tarefas?view=calendar', icone: Calendar },
        ]} />
      )}

      <TaskModal
        isOpen={novaCategoria !== null}
        onClose={() => setNovaCategoria(null)}
        onTaskCreated={() => { setNovaCategoria(null); recarregar(); }}
        initialCategoria={novaCategoria ?? undefined}
      />
    </div>
  );
};

export default TelaPadrao;
